import { RouteComponentProps } from 'react-router-dom';
import { isEqual } from 'lodash';

import {
  CarryOnBagType,
  CreditEligibleFilterType,
  FilterPolicyType,
  FlightsFilterType,
  FlightsSearchFilterValuesInput,
  FlightsSortOptionTypeInput,
  FlightsSortOrder,
  FlightsSortType,
  FlightsStopFilterOperation,
  FlightsTimeOfDay,
  NoCancelFeeType,
  SeatChoiceType
} from '__generated__/api';
import TriggerFlightsSearchParams from 'types/shared/trigger-flights-search-params';
import { closeDialogs } from 'utility';
import { SearchCriteriaStore, SortFilterStore } from 'stores';
import { ContextStore } from 'bernie-plugin-mobx';
import { Field } from 'uitk-react-form';
import { updateAddonRequestedList } from '../Air_Smart_Shopping_44809/utils';

export interface FilterItem {
  selected: boolean;
  name: FlightsFilterType;
  value: string;
  id: string;
}

export interface ShoppingSearchCriteria {
  selections: {
    __typename: 'SelectedValue';
    id: FlightsFilterType;
    value: string;
  }[];
}

const getFilterInput = (name: FlightsFilterType, value: string): FlightsSearchFilterValuesInput => {
  const {
    NUM_OF_STOPS,
    PREFERRED_AIRLINE,
    ARRIVAL_TIME,
    DEPARTURE_TIME,
    NO_CANCEL_FEE,
    CARRY_ON_BAG,
    CHECKED_BAG,
    SEAT_CHOICE,
    FLEXIBLE_CHANGE_POLICIES,
    CREDIT_ELIGIBLE,
    ARRIVAL_AIRPORT,
    DEPARTURE_AIRPORT
  } = FlightsFilterType;

  switch (name) {
    case NUM_OF_STOPS:
      return {
        numOfStopFilterValue: {
          stopInfo: {
            numberOfStops: parseInt(value),
            stopFilterOperation:
              parseInt(value) < 2 ? FlightsStopFilterOperation.EQUAL : FlightsStopFilterOperation.GREATER_THAN_EQUAL
          }
        }
      };
    case PREFERRED_AIRLINE:
      return {
        preferredAirlineFilterValue: {
          carrierCode: value
        }
      };
    case ARRIVAL_TIME:
      return {
        arrivalTimeFilterValue: {
          timeOfDay: value as FlightsTimeOfDay
        }
      };
    case DEPARTURE_TIME:
      return {
        departureTimeFilterValue: {
          timeOfDay: value as FlightsTimeOfDay
        }
      };
    case SEAT_CHOICE:
      return {
        seatChoiceBasedFilterValue: {
          seatChoice: value as SeatChoiceType
        }
      };
    case CARRY_ON_BAG:
      return {
        carryOnBagBasedFilterValue: {
          carryOnBag: value as CarryOnBagType
        }
      };
    case NO_CANCEL_FEE:
      return {
        noCancelFeeBasedFilterValue: {
          noCancelFee: value as NoCancelFeeType
        }
      };
    // case NO_CHANGE_FEE:
    //   return {
    //     noChangeFeeBasedFilterValue: {
    //       noChangeFee: value as NoChangeFeeType
    //     }
    //   };
    case FLEXIBLE_CHANGE_POLICIES:
      return {
        flexibleChangePolicyBasedFilterValue: {
          policyName: value as FilterPolicyType
        }
      };
    case CREDIT_ELIGIBLE:
      return {
        creditEligibleFilterValue: {
          creditEligible: CreditEligibleFilterType.CREDIT_ELIGIBLE
        }
      };
    case ARRIVAL_AIRPORT:
      return {
        arrivalAirportCodeFilterValue: {
          airportCode: value
        }
      };
    case DEPARTURE_AIRPORT:
      return {
        departureAirportCodeFilterValue: {
          airportCode: value
        }
      };
    case CHECKED_BAG:
      return {
        checkedBagBasedFilterValue: {
          numOfCheckedBags: parseInt(value)
        }
      };
    default:
      return;
  }
};

export const updateSearchFilterValuesList = (
  filterItem: FilterItem,
  searchFilterValuesList: FlightsSearchFilterValuesInput[]
) => {
  const { name, value, selected } = filterItem;
  const filterInput = getFilterInput(name, value);
  if (selected) {
    searchFilterValuesList.push(filterInput);
  } else {
    searchFilterValuesList.splice(
      0,
      searchFilterValuesList.length,
      ...searchFilterValuesList.filter(currentFilter => !isEqual(currentFilter, filterInput))
    );
  }
};

const updateFilterCallback = (
  filterItem: FilterItem,
  searchCriteriaStore: SearchCriteriaStore,
  triggerSearchParams: TriggerFlightsSearchParams,
  shouldExecuteSearch?: boolean
) => {
  const { searchFilterValuesList } = searchCriteriaStore.searchCriteria;
  updateSearchFilterValuesList(filterItem, searchFilterValuesList as FlightsSearchFilterValuesInput[]);
  if (!shouldExecuteSearch) {
    searchCriteriaStore.triggerSearch(triggerSearchParams);
  }
};

export const onFilterSelect = (
  filterItem: FilterItem,
  event: { target: { id: string; checked: boolean } },
  isDialog: boolean,
  searchCriteriaStore: SearchCriteriaStore,
  context: ContextStore,
  history: RouteComponentProps['history'],
  location: RouteComponentProps['location'],
  sortAndFilterStore: SortFilterStore
) => {
  filterItem.id = event.target.id;
  filterItem.selected = event.target.checked;
  if (sortAndFilterStore.isDefaultSortApplied()) {
    searchCriteriaStore.searchCriteria.sortOption = null;
  }
  searchCriteriaStore.requestToast = undefined;

  const triggerSearchParams: TriggerFlightsSearchParams = {
    history,
    location,
    context,
    shouldForce: true,
    fetchFlexResults: false,
    fetchSearchForm: false
  };

  if (isDialog) {
    updateFilterCallback(filterItem, searchCriteriaStore, triggerSearchParams, isDialog);
  } else {
    closeDialogs({
      history,
      location,
      callback: () => updateFilterCallback(filterItem, searchCriteriaStore, triggerSearchParams)
    });
  }
};

const removeFilterPill = (filter: ShoppingSearchCriteria, searchFilterValuesList: FlightsSearchFilterValuesInput[]) => {
  const nextSearchFilterValuesList: FlightsSearchFilterValuesInput[] = [];
  const filterInputs: FilterItem[] = filter.selections.map(filter => {
    return {
      name: filter.id,
      value: filter.value,
      selected: true,
      id: filter.id + '-' + filter.value
    };
  });
  filterInputs.forEach(filterInput => {
    const { name, value } = filterInput;
    nextSearchFilterValuesList.push(getFilterInput(name, value));
  });
  searchFilterValuesList.splice(0, searchFilterValuesList.length, ...nextSearchFilterValuesList);
};

const isPilterForSmartShopping = (smartShoppingEnabled: boolean, filterValue: FlightsSearchFilterValuesInput) =>
  smartShoppingEnabled &&
  (filterValue.checkedBagBasedFilterValue ||
    filterValue.carryOnBagBasedFilterValue ||
    filterValue.seatChoiceBasedFilterValue);

export const clearSearchFilterValuesList = (
  smartShoppingEnabled: boolean,
  searchFilterValuesList: FlightsSearchFilterValuesInput | FlightsSearchFilterValuesInput[]
) => {
  const selectedFilters = searchFilterValuesList instanceof Array ? searchFilterValuesList : [searchFilterValuesList];

  return selectedFilters.filter(filterValue => isPilterForSmartShopping(smartShoppingEnabled, filterValue));
};

const onRemoveAllAppliedFilters = (
  searchCriteriaStore: SearchCriteriaStore,
  context: ContextStore,
  history: RouteComponentProps['history'],
  location: RouteComponentProps['location']
) => {
  searchCriteriaStore.searchCriteria.searchFilterValuesList = clearSearchFilterValuesList(
    searchCriteriaStore.hasSmartShoppingCriteria,
    searchCriteriaStore.searchCriteria.searchFilterValuesList
  );

  const triggerSearchParams: TriggerFlightsSearchParams = {
    history,
    location,
    context,
    shouldForce: true,
    fetchFlexResults: false,
    fetchSearchForm: false
  };
  searchCriteriaStore.triggerSearch(triggerSearchParams);
};

export const selectClearAllFilters = (
  searchCriteriaStore: SearchCriteriaStore,
  context: ContextStore,
  history: RouteComponentProps['history'],
  location: RouteComponentProps['location']
) => {
  closeDialogs({
    history,
    location,
    callback: () => onRemoveAllAppliedFilters(searchCriteriaStore, context, history, location)
  });
};

const onRemoveAppliedFilter = (
  filter: ShoppingSearchCriteria,
  searchCriteriaStore: SearchCriteriaStore,
  context: ContextStore,
  history: RouteComponentProps['history'],
  location: RouteComponentProps['location']
) => {
  const { searchFilterValuesList } = searchCriteriaStore.searchCriteria;
  removeFilterPill(filter, searchFilterValuesList as FlightsSearchFilterValuesInput[]);
  const triggerSearchParams: TriggerFlightsSearchParams = {
    history,
    location,
    context,
    shouldForce: true,
    fetchFlexResults: false,
    fetchSearchForm: false
  };
  searchCriteriaStore.triggerSearch(triggerSearchParams);
};

export const removePill = (
  filter: ShoppingSearchCriteria,
  searchCriteriaStore: SearchCriteriaStore,
  context: ContextStore,
  history: RouteComponentProps['history'],
  location: RouteComponentProps['location']
) => {
  closeDialogs({
    history,
    location,
    callback: () => onRemoveAppliedFilter(filter, searchCriteriaStore, context, history, location)
  });
};

export const getSortType = (value: string): FlightsSortOptionTypeInput => {
  if (value === FlightsSortType.BEST_BETS || value === FlightsSortType.BEST_SORT || value === FlightsSortType.NONSTOP) {
    return null;
  }
  const index = value.lastIndexOf('_');
  const sortType = value.slice(0, index);
  const sortOrder = value.slice(index + 1);

  return {
    sortOrder: FlightsSortOrder[FlightsSortOrder[sortOrder]],
    sortType: FlightsSortType[FlightsSortType[sortType]]
  };
};

export const triggerSortSearchCall = (
  field: Field,
  searchCriteriaStore: SearchCriteriaStore,
  context: ContextStore,
  history: RouteComponentProps['history'],
  location: RouteComponentProps['location']
) => {
  searchCriteriaStore.requestToast = undefined;
  searchCriteriaStore.searchCriteria.sortOption = getSortType(field.value);
  const triggerSearchParams: TriggerFlightsSearchParams = {
    history,
    location,
    context,
    shouldForce: true,
    fetchFlexResults: false,
    fetchSearchForm: false
  };
  searchCriteriaStore.triggerSearch(triggerSearchParams);
};

export const triggerSubmitSearchCall = (
  searchCriteriaStore: SearchCriteriaStore,
  context: ContextStore,
  history: RouteComponentProps['history'],
  location: RouteComponentProps['location']
) => {
  const { searchCriteria, dialogSortAndFilter } = searchCriteriaStore;
  searchCriteria.searchFilterValuesList = JSON.parse(JSON.stringify(dialogSortAndFilter.searchFilterValuesList));
  if (dialogSortAndFilter.sortOption) {
    searchCriteria.sortOption = Object.assign({}, dialogSortAndFilter.sortOption);
  } else {
    searchCriteria.sortOption = null;
  }

  const triggerSearchParams: TriggerFlightsSearchParams = {
    history,
    location,
    context,
    shouldForce: true,
    fetchFlexResults: false,
    fetchSearchForm: false
  };
  closeDialogs({
    history,
    location,
    callback: () => searchCriteriaStore.triggerSearch(triggerSearchParams)
  });
};

export const onSortFilterSelect = (
  filterItem: FilterItem,
  event: { target: { id: string; checked: boolean } },
  searchCriteriaStore: SearchCriteriaStore
) => {
  filterItem.id = event.target.id;
  filterItem.selected = event.target.checked;
  searchCriteriaStore.requestToast = undefined;
  if (filterItem.name.toString() === 'SORT') {
    searchCriteriaStore.dialogSortAndFilter.sortOption = getSortType(filterItem.value);
  } else {
    updateSearchFilterValuesList(
      filterItem,
      searchCriteriaStore.dialogSortAndFilter.searchFilterValuesList as FlightsSearchFilterValuesInput[]
    );
  }
};

export const onFilterPillSelect = (
  field: Field,
  event: { target: { id: string; value: FlightsFilterType; checked: boolean } },
  searchCriteriaStore: SearchCriteriaStore,
  context: ContextStore,
  history: RouteComponentProps['history'],
  location: RouteComponentProps['location'],
  displayToast: boolean
) => {
  const {
    target: { value, checked }
  } = event;
  const fieldName = parseInt(value) === 1 ? FlightsFilterType.CHECKED_BAG : value;
  const filterItem = {
    id: field.name,
    selected: checked,
    name: fieldName,
    value: value
  };

  const triggerSearchParams: TriggerFlightsSearchParams = {
    history,
    location,
    context,
    shouldForce: true,
    fetchFlexResults: false,
    fetchSearchForm: false
  };

  displayToast ? (searchCriteriaStore.requestToast = fieldName) : (searchCriteriaStore.requestToast = undefined);
  updateFilterCallback(filterItem, searchCriteriaStore, triggerSearchParams);
  updateAddonRequestedList(filterItem, searchCriteriaStore.sideSheetQueryContext.addOnRequested);
};
